import { FormattedMessage, useIntl } from 'react-intl';
import Alert from 'client/components/ui/Alert/Alert';
import Modal from 'client/components/ui/Modal/Modal';
import Button from 'client/components/aria/Button/Button';
import styles from 'client/pages/MapPage/components/EnhancedMeasuresModal/EnhancedMeasuresModal.module.scss';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAOIMode, setMenuOpen, setMenuSelectedTab } from 'client/store/mapSlice/mapSlice';
import { mapMenuTabKeys, mapModes } from 'client/utils/constants/appStates';
import { mdiClose, mdiMapMarkerAlert } from '@mdi/js';
import Icon from '@mdi/react';
import { useEnhancedMeasures } from './useEnhancedMeasures';

export const EnhancedMeasuresModal = ({ zIndex }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    // when false, pending
    // when true, load assets and calculate counts
    const isAoiMode = useSelector(selectAOIMode);
    const mapMode = useSelector(state => state?.map?.mode) || [];

    const { sensitiveAssetsCount, assetOwners, redactedAssetsCount, enhancedMeasuresSignature } = useEnhancedMeasures();
    const dismissedSignatureRef = useRef(null);

    const dismissedForCurrentMeasures =
        dismissedSignatureRef.current != null &&
        dismissedSignatureRef.current === enhancedMeasuresSignature;

    const [showing, setShowing] = useState(false);

    // If the AOI is cleared (back to DEFAULT mode), allow prompting again
    // even for the same enhancedMeasuresSignature.
    useEffect(() => {
        const isCleared = mapMode.length === 1 && mapMode[0] === mapModes.DEFAULT;
        if (isCleared) {
            dismissedSignatureRef.current = null;
            setShowing(false);
        }
    }, [mapMode]);

    const shouldPrompt = Boolean(
        isAoiMode &&
        sensitiveAssetsCount > 0 &&
        !dismissedForCurrentMeasures
    );

    useEffect(() => {
        if (!shouldPrompt) {
            setShowing(false);
            return;
        }

        const timeout = setTimeout(() => setShowing(true), 200);
        return () => clearTimeout(timeout);
    }, [shouldPrompt]);

    const close = () => {
        dismissedSignatureRef.current = enhancedMeasuresSignature;
        setShowing(false);
    };

    const acknowledge = () => {
        dismissedSignatureRef.current = enhancedMeasuresSignature;
        setShowing(false);
        dispatch(setMenuOpen(true));
        dispatch(setMenuSelectedTab(mapMenuTabKeys.ENHANCED_MEASURES));
    };

    return <Modal
        zindex={zIndex}
        data-testid="enhancedMeasures.modal"
        open={showing}
    >
        <Alert data-testid="enhancedMeasures.modal.alert" className={styles.container}>
            <div className={styles.header}>
                <h1 className={['govuk-label-wrapper', styles.heading].join(' ')} autoFocus data-testid="enhancedMeasures.modal.heading">
                    <label className={['govuk-label govuk-label--l', styles.label].join(' ')} htmlFor="create.aoi.dialog.alert">
                        <FormattedMessage id='enhancedMeasures.modal.title' />
                    </label>
                </h1>
                <Button
                    data-testid="enhancedMeasures.modal.close.button"
                    aria-label={intl.formatMessage({ id: 'close' })}
                    variant="transparent"
                    className={styles.button}
                    onPress={close}>
                    <Icon path={mdiClose} size={1.5} />
                </Button>
            </div>
            <div className={['govuk-form-group', styles.form].join(' ')} tabIndex={-1}>
                <hgroup>
                    <div className={styles.icon}>
                        <Icon path={mdiMapMarkerAlert} size={1.5} />
                    </div>
                    <h2 data-testid="enhancedMeasures.modal.summary">
                        {intl.formatMessage(
                            {
                                id: assetOwners.length === 1
                                    ? 'enhancedMeasures.modal.summary.singular'
                                    : 'enhancedMeasures.modal.summary.plural'
                            },
                            { count: assetOwners.length }
                        )}
                    </h2>
                    {redactedAssetsCount > 0
                        ? <p className="govuk-hint" data-testid="enhancedMeasures.modal.redactedSummary">
                            {intl.formatMessage(
                                {
                                    id: redactedAssetsCount === 1
                                        ? 'enhancedMeasures.modal.redactedSummary.singular'
                                        : 'enhancedMeasures.modal.redactedSummary.plural'
                                },
                                { count: redactedAssetsCount }
                            )}
                        </p>
                        : null
                    }
                </hgroup>
                <div className={styles.actions}>
                    <Button
                        data-testid="enhancedMeasures.modal.close.link"
                        variant="link"
                        onPress={close}
                    >
                        {intl.formatMessage({ id: 'close' })}
                    </Button>
                    <Button
                        data-testid="enhancedMeasures.modal.review"
                        variant="success"
                        onPress={acknowledge}
                    >
                        {intl.formatMessage({ id: 'enhancedMeasures.modal.review' })}
                    </Button>
                </div>
            </div>
        </Alert>
    </Modal>;
};
